// In-app browsers (Facebook / Instagram / TikTok WebViews) and older iOS Safari
// hand back a picked `File` that is only a thin reference to OS-managed bytes.
// When the WebView backgrounds or hits memory pressure between when the photo is
// added and when it is uploaded, that reference is invalidated while the JS heap
// stays alive, so the PUT to MinIO reads a dead body and fails with a generic
// ERR_NETWORK (status 0) for every file at once. Copying the bytes into a
// memory-backed File at add-time detaches them from the OS handle so the upload
// survives. See docs/superpowers/specs/2026-07-24-inapp-browser-upload-byte-snapshot.md.

// Cap on total bytes held as in-memory snapshots between add and upload. Kept
// deliberately mobile-safe: the users who hit the dead-handle bug are large-batch
// in-app-browser users on low-end Android, who are also the ones a big in-memory
// hold would OOM. At ~80 MB the common in-app order (a handful to ~20 shared
// photos) is fully protected, while a 100-photo batch snapshots the first ~20 and
// the rest fall back to the live handle — i.e. today's behavior, no added OOM
// risk, and per-file-settle still rescues the snapshotted ones instead of losing
// the whole order. For huge batches the durable fix is uploading as photos are
// added; see docs/superpowers/specs/2026-07-24-inapp-browser-upload-byte-snapshot.md.
export const MAX_SNAPSHOT_BYTES = 80 * 1024 * 1024; // 80 MB

/**
 * Kill-switch for add-time byte snapshotting. On by default; set
 * NEXT_PUBLIC_UPLOAD_BYTE_SNAPSHOT="false" to turn it off, in which case the
 * upload falls back to the live file handle (pre-fix behavior).
 */
export function isSnapshotEnabled(): boolean {
  return process.env.NEXT_PUBLIC_UPLOAD_BYTE_SNAPSHOT !== "false";
}

/**
 * How long to wait for a picked file's bytes before giving up on it.
 *
 * With "Optimise iPhone Storage", iOS keeps the full photo in iCloud and only
 * the metadata on the device. The picker hands back a File with a real name,
 * type and size; asking for its bytes makes iOS fetch it from iCloud first.
 * That fetch can take a long time, or never finish at all.
 *
 * `await file.arrayBuffer()` had no timeout and the caller copies files
 * sequentially, so a single undownloaded photo froze the whole batch with no
 * error and no telemetry — the customer saw thumbnails appear and then nothing,
 * indefinitely. Two customers reported precisely that in early September 2026.
 *
 * 20s is chosen to be longer than any plausible local read (those are
 * milliseconds) while still bounded. A photo that exceeds it is not discarded:
 * see SnapshotOutcome — a slow file keeps its live handle and can still upload.
 */
export const SNAPSHOT_READ_TIMEOUT_MS = 20_000;

/**
 * Why a snapshot did not produce bytes — the two cases need opposite handling.
 *
 * `unreadable` is terminal: iOS reclaimed the handle or the object is gone
 * (`NotFoundError: The object can not be found here`). Uploading it would fail,
 * so the caller drops it.
 *
 * `timeout` is NOT terminal. The bytes may simply still be coming down from
 * iCloud. Dropping the photo would lose something the customer chose and might
 * well upload fine, so the caller keeps the live handle instead.
 */
export type SnapshotOutcome =
  | { status: "ok"; file: File }
  | { status: "unreadable" }
  | { status: "timeout" };

export async function snapshotFileBytesResult(
  file: File,
  timeoutMs: number = SNAPSHOT_READ_TIMEOUT_MS,
): Promise<SnapshotOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<SnapshotOutcome>((resolve) => {
    timer = setTimeout(() => resolve({ status: "timeout" }), timeoutMs);
  });

  const read = (async (): Promise<SnapshotOutcome> => {
    try {
      const buf = await file.arrayBuffer();
      return {
        status: "ok",
        file: new File([buf], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        }),
      };
    } catch {
      return { status: "unreadable" };
    }
  })();

  try {
    return await Promise.race([read, timedOut]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
/**
 * Total bytes currently held as in-memory snapshots across a files list. Used to
 * keep MAX_SNAPSHOT_BYTES a global ceiling (reconciled on every add/remove/clear)
 * rather than a per-add-action one.
 */
export function heldSnapshotBytes(
  files: ReadonlyArray<{ bytes?: { size: number } }>,
): number {
  return files.reduce((sum, f) => sum + (f.bytes?.size ?? 0), 0);
}

/** True when snapshotting this file keeps the held-bytes total within the cap. */
export function withinSnapshotBudget(
  currentSnapshotBytes: number,
  fileSize: number,
  cap: number = MAX_SNAPSHOT_BYTES,
): boolean {
  return currentSnapshotBytes + fileSize <= cap;
}

/**
 * The payload to upload: the detached snapshot when we have one, otherwise the
 * live handle (backward-compatible fallback when snapshotting was skipped/off).
 */
export function pickUploadSource(file: { file: File; bytes?: File }): File {
  return file.bytes ?? file.file;
}

/**
 * After this many consecutive timeouts, stop being patient with the rest of the
 * batch. A run of timeouts means the library is cloud-backed, not that one
 * photo is unlucky.
 */
export const PATIENCE_ENDS_AFTER_CONSECUTIVE_TIMEOUTS = 3;

/**
 * A brief look, used once patience has run out.
 *
 * We keep probing rather than skipping the remaining photos outright: a photo
 * that is actually on the device copies in milliseconds, and snapshotting is
 * what protects it from an in-app WebView (Instagram/Facebook) reclaiming its
 * handle before checkout. Skipping the rest of the batch would withdraw that
 * protection from photos that never had a problem, just because three earlier
 * ones were in iCloud. A second is far longer than any local read and cheap
 * enough to pay per photo.
 */
export const SNAPSHOT_PROBE_TIMEOUT_MS = 1_000;

/**
 * Advances the run-of-timeouts counter for one snapshot outcome.
 *
 * Kept as a pure function, and tested, deliberately: the previous fix in this
 * area lost photos in production because equivalent bookkeeping sat inline in a
 * loop where no test could reach it, and a one-way latch went unnoticed.
 *
 * - `timeout`  extends the run.
 * - `ok`       clears it — proof the library is on the device, so be patient again.
 * - `unreadable` leaves it alone: a dead handle is not evidence about iCloud, and
 *   clearing it would let an alternating timeout/unreadable batch stay patient
 *   forever, paying the full timeout every other photo.
 */
export function nextConsecutiveTimeouts(
  previous: number,
  status: SnapshotOutcome["status"],
): number {
  if (status === "timeout") return previous + 1;
  if (status === "ok") return 0;
  return previous;
}

/** How long to wait for the next photo, given how many just timed out in a row. */
export function snapshotTimeoutFor(consecutiveTimeouts: number): number {
  return consecutiveTimeouts >= PATIENCE_ENDS_AFTER_CONSECUTIVE_TIMEOUTS
    ? SNAPSHOT_PROBE_TIMEOUT_MS
    : SNAPSHOT_READ_TIMEOUT_MS;
}
